<script lang="ts" setup>
import type { ComputedRef, Ref } from "vue"
import { autoUpdate, flip, useFloating } from "@floating-ui/vue"
import { onClickOutside } from "@vueuse/core"
import { useField } from "vee-validate"
import { computed, nextTick, ref, watch, watchEffect } from "vue"
import { Chip, Hint, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array<string>,
    default: [],
  },
  existingTags: {
    type: Array<string>,
    default: [],
  },
  placeholder: {
    type: String,
    default: "",
  },
  error: {
    type: String,
    default: "",
  },
  hint: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disableSuggestions: {
    type: Boolean,
    default: false,
  },
  removeTagOnBackspace: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(["addTag", "deleteTag"])

const { t } = useTranslation()

const { setErrors, errorMessage } = useField("tags")

const tags: Ref<Array<string>> = ref(props.modelValue)
const inputText: Ref<string> = ref("")
const suggestions: Ref<Array<string>> = ref([])

const tagInputRef: Ref<HTMLElement | null> = ref(null)

function focusInput() {
  tagInputRef.value?.focus()
}

watch(
  () => props.isLoading,
  newValue => !newValue && nextTick(() => focusInput()),
)

function removeTag(index: number) {
  if (props.disabled || props.readonly)
    return
  const removedItem = tags.value.splice(index, 1)[0]
  emit("deleteTag", removedItem)
}

function removeLastTag(event: KeyboardEvent) {
  const target = event.target as HTMLInputElement

  if (target.value.length === 0 && props.removeTagOnBackspace) {
    removeTag(tags.value.length - 1)
  }
}

function addTag(inputValue: string) {
  const val = inputValue.trim()
  if (checkValueHasErrors(val))
    return

  tags.value.push(val)
  emit("addTag", val)
  inputText.value = ""
  setErrors("")
}

function checkValueHasErrors(value: string): boolean {
  const lowerCaseValue = value.toLowerCase()

  if (!lowerCaseValue.length)
    return true

  if (lowerCaseValue.length > 30) {
    setErrors(t("tags_input.error.max"))
    return true
  }

  if (lowerCaseValue.includes(" ")) {
    setErrors(t("tags_input.error.space"))
    return true
  }

  if (tags.value.map(tag => tag.toLowerCase()).includes(lowerCaseValue)) {
    setErrors(t("tags_input.error.duplicate"))
    return true
  }

  setErrors("")
  return false
}

const hasErrors: ComputedRef<boolean> = computed(() => {
  return !!props.error || !!errorMessage.value
})

watchEffect(() => {
  checkValueHasErrors(inputText.value.trim())
  if (inputText.value.trim()) {
    suggestions.value = props.existingTags.filter(
      tag => tag.toLowerCase().startsWith(inputText.value.toLowerCase()) && !tags.value.includes(tag),
    )
  }
  else {
    suggestions.value = []
  }
})

const suggestionItemsVisible: Ref<boolean> = ref(false)
watchEffect(() => {
  if (suggestions.value.length && !props.disableSuggestions)
    suggestionItemsVisible.value = true
  else suggestionItemsVisible.value = false
})

const suggestionItemsRef: Ref<HTMLElement | null> = ref(null)
const tagInputWrapperRef: Ref<HTMLElement | null> = ref(null)
const { floatingStyles } = useFloating(tagInputWrapperRef, suggestionItemsRef, {
  placement: "bottom-start",
  whileElementsMounted: autoUpdate,
  middleware: [flip({ crossAxis: false })],
})

onClickOutside(tagInputWrapperRef, () => {
  suggestionItemsVisible.value = false
})
</script>

<template>
  <div class="gpx-tag-input-container">
    <label for="tags">
      <div
        ref="tagInputWrapperRef"
        class="gpx-tag-input-wrapper"
        :class="{
          'gpx-tag-input-wrapper_is-floating': tags.length > 0,
          'gpx-tag-input-wrapper_disabled': disabled,
          'gpx-tag-input-wrapper_error': hasErrors,
        }"
      >
        <TransitionGroup class="flex gap-[5px] flex-wrap items-center overflow-auto" name="gpx-chip" tag="div">
          <Chip
            v-for="(tag, index) in tags"
            :key="tag"
            variant="tag"
            :label="tag"
            :clearable="!disabled && !readonly"
            :disabled="disabled"
            :is-loading="isLoading"
            @delete="() => removeTag(index)"
          />

          <div key="input">
            <Icon v-if="isLoading" icon="Loading" />
            <input
              v-if="!readonly && !disabled && !isLoading"
              id="tags"
              ref="tagInputRef"
              v-model="inputText"
              name="tags"
              type="text"
              :placeholder="tags.length > 0 ? t('tags_input.placeholder') : ''"
              class="gpx-tag-input"
              :class="{ 'gpx-tag-input_error': hasErrors, 'gpx-tag-input_padding': tags.length === 0 }"
              @keydown.enter.prevent="(event) => addTag((event.target as HTMLInputElement).value)"
              @keydown.delete="removeLastTag"
            >

            <span
              class="gpx-floating-label"
              :class="{
                'gpx-floating-label_is-floating': tags.length > 0 || inputText,
                'gpx-floating-label_error': hasErrors,
              }"
            >
              {{ label }}
            </span>
          </div>
        </TransitionGroup>
      </div>
      <ul
        v-if="suggestionItemsVisible && suggestions.length"
        ref="suggestionItemsRef"
        class="gpx-suggestion-items z-20 rounded-sm"
        :style="floatingStyles"
      >
        <li class="gpx-suggestion-item-header">{{ t("tags_input.suggestions") }}</li>
        <li v-for="(suggestion, index) in suggestions" :key="index">
          <button
            class="gpx-suggestion-item-button"
            @click.prevent="() => addTag(suggestion)"
            @focusout="index + 1 === tags.length && (suggestionItemsVisible = false)"
          >
            {{ suggestion }}
          </button>
        </li>
      </ul>
    </label>
    <span v-if="hasErrors" class="gpx-field-error">{{ error || errorMessage }}</span>
    <Hint v-if="hint" class="mt-2" :hint="hint" />
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css";

.gpx-tag-input-container {
  @apply relative;
}

.gpx-tag-input-wrapper {
  @apply py-4 px-3 overflow-auto flex flex-wrap gap-1 rounded-lg items-center bg-white border border-gray-300;
}

.gpx-tag-input-wrapper_error {
  @apply border-danger;
}

.gpx-tag-input-wrapper_disabled {
  @apply bg-gray-200 cursor-not-allowed;
}

.gpx-tag-input-wrapper_is-floating {
  @apply pt-6;
}

.gpx-floating-label {
  transform: translateY(-50%);
  top: 27px;
  left: 1rem;
  transform-origin: left top;
  transition:
    transform 150ms,
    color 150ms,
    top 500ms cubic-bezier(0.4, 0, 0.2, 1);

  @apply block  absolute text-gray-500 select-none;
}

.gpx-tag-input {
  border: none;
  outline: none;
  font-size: 1em;
}

.gpx-tag-input:focus + .gpx-floating-label {
  @apply text-gray-700;
}

.gpx-tag-input_error {
  @apply text-danger;
}

.gpx-tag-input:focus + .gpx-floating-label,
.gpx-floating-label_is-floating {
  transform: translateY(-100%) scale(0.75);
}

.gpx-tag-input_padding:focus {
  padding-top: 7px;
}

.gpx-chip {
  transition:
    opacity 0.25s ease-in-out,
    transform 0.25s ease-in-out;
  opacity: 1;
  transform: scale(1);
}

.gpx-chip-enter-active,
.gpx-chip-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.gpx-chip-enter-from,
.gpx-chip-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* Suggestion Items */

.gpx-suggestion-items {
  width: 100%;
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  max-height: 20rem;
  overflow-y: auto;

  @apply shadow-lg border border-gray-300 bg-white;
}

.gpx-suggestion-item-header {
  font-size: smaller;
  padding: 0.5rem;
  font-weight: 800;
}

.gpx-suggestion-item-button {
  width: 100%;
  height: 100%;
  padding: 0.5rem;
  text-align: start;
  border: none;
  transition: background-color 0.1s ease;

  @apply text-black bg-transparent;
}

.gpx-suggestion-item-button:hover {
  @apply bg-gray-100 cursor-pointer;
}
</style>
